import React, { useMemo } from "react";
import { BarChart3 } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, LogarithmicScale, BarElement, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend, Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Doughnut, Line, Scatter } from "react-chartjs-2";

ChartJS.register(
  CategoryScale, LinearScale, LogarithmicScale, BarElement, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend, Filler, ChartDataLabels,
);

ChartJS.defaults.color = "#64748b";
ChartJS.defaults.font.family = "system-ui, -apple-system, sans-serif";

const G = "#22c55e", R = "#ef4444", B = "#2563eb", A = "#f59e0b", T = "#0f766e", V = "#7c3aed", M = "#64748b", GR = "rgba(148,163,184,0.25)";

const bg = (c, o = 0.7) => `rgba(${parseInt(c.slice(1,3),16)},${parseInt(c.slice(3,5),16)},${parseInt(c.slice(5,7),16)},${o})`;
const sc = (v) => Number(v) >= 0 ? "text-green-400" : "text-red-400";
const scBg = (v) => Number(v) >= 0 ? G : R;
const fmt = (v, d) => v == null || isNaN(v) ? "-" : Number(v).toLocaleString(undefined, { minimumFractionDigits: d||0, maximumFractionDigits: d||2 });
const fmtS = (v, s) => v == null || isNaN(v) ? "-" : (Number(v)>0?"+":"") + Number(v).toFixed(2) + (s||"");
const fmtP = (v) => v == null || isNaN(v) ? "-" : Number(v).toFixed(1)+"%";

const noDataMsg = (msg) => (
  <div className="flex items-center justify-center h-full text-ics-gray text-sm">{msg || "No data available"}</div>
);

const chartOpts = (extra = {}) => ({
  responsive: true, maintainAspectRatio: true,
  plugins: { legend: { labels: { color: "#94a3b8", boxWidth: 12, padding: 10 } }, datalabels: { display: false }, ...extra.plugins },
  scales: { x: { ticks: { color: M, font: { size: 10 } }, grid: { color: GR } }, y: { ticks: { color: M, font: { size: 10 } }, grid: { color: GR } }, ...extra.scales },
});

const compactOpts = (extra = {}) => ({ ...chartOpts(extra), plugins: { ...chartOpts(extra).plugins, legend: { display: false } } });

function Panel({ title, subtitle, children, className = "" }) {
  return (
    <div className={`bg-white/5 rounded-xl p-4 border border-white/10 ${className}`}>
      <div className="fw-bold text-ics-white mb-1 text-sm">{title}</div>
      {subtitle && <div className="text-xs text-ics-gray mb-2">{subtitle}</div>}
      {children}
    </div>
  );
}

function Kpi({ label, value, hint, color }) {
  return (
    <div className="bg-ics-navy-light rounded-xl p-3 border border-white/5">
      <div className="text-xs text-ics-gray font-semibold uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-extrabold mt-0.5 ${color || "text-ics-white"}`}>{value}</div>
      {hint != null && <div className="text-xs text-ics-gray mt-0.5">{hint}</div>}
    </div>
  );
}

export default function NasdaqReportDashboard({ data, loading }) {
  const o = data?.overall || {};
  const eq = data?.equity_summary || {};
  const tickers = data?.tickers || [];
  const streaks = data?.streaks || {};
  const resSummary = data?.resolution_summary || {};
  const topTickers = data?.top_tickers || [];
  const bottomTickers = data?.bottom_tickers || [];

  const monthlyChart = useMemo(() => {
    const m = data?.monthly || [];
    if (!m.length) return null;
    return { labels: m.map(r => r.label), datasets: [{ label: "ROI %", data: m.map(r => r.total_roi), backgroundColor: m.map(r => scBg(r.total_roi)), borderRadius: 4 }] };
  }, [data]);

  const outcomeChart = useMemo(() => {
    const o = data?.outcomes || [];
    if (!o.length) return null;
    return { labels: o.map(r => r.label), datasets: [{ data: o.map(r => r.total), backgroundColor: [G, R, A, B, V, T, "#94a3b8"], borderWidth: 0 }] };
  }, [data]);

  const equityChart = useMemo(() => {
    let e = data?.equity || [];
    if (e.length > 200) { const s = Math.ceil(e.length / 200); e = e.filter((_, i) => i % s === 0 || i === e.length - 1); }
    if (!e.length) return null;
    return { labels: e.map(r => r.date), datasets: [{ label: "Equity %", data: e.map(r => r.equity), borderColor: B, backgroundColor: bg(B, 0.12), fill: true, pointRadius: 0, tension: 0.2 }] };
  }, [data]);

  const dailyChart = useMemo(() => {
    let d = data?.daily || [];
    if (d.length > 200) { const s = Math.ceil(d.length / 200); d = d.filter((_, i) => i % s === 0 || i === d.length - 1); }
    if (!d.length) return null;
    return { labels: d.map(r => r.date), datasets: [{ label: "Daily ROI", data: d.map(r => r.daily_profit), backgroundColor: d.map(r => scBg(r.daily_profit)), borderRadius: 2 }] };
  }, [data]);

  const drawdownChart = useMemo(() => {
    let e = data?.equity || [];
    if (e.length > 200) { const s = Math.ceil(e.length / 200); e = e.filter((_, i) => i % s === 0 || i === e.length - 1); }
    if (!e.length) return null;
    return { labels: e.map(r => r.date), datasets: [{ label: "Drawdown", data: e.map(r => r.drawdown), borderColor: R, backgroundColor: bg(R, 0.18), fill: true, pointRadius: 0, tension: 0.2 }] };
  }, [data]);

  const rsiChart = useMemo(() => {
    const r = data?.rsi || [];
    if (!r.length) return null;
    return { labels: r.map(x => x.label), datasets: [{ label: "ROI %", data: r.map(x => x.total_roi), backgroundColor: r.map(x => scBg(x.total_roi)), borderRadius: 3 }] };
  }, [data]);

  const weekdayChart = useMemo(() => {
    const w = data?.weekday || [];
    if (!w.length) return null;
    return { labels: w.map(x => x.label), datasets: [{ label: "ROI %", data: w.map(x => x.total_roi), backgroundColor: w.map(x => scBg(x.total_roi)), borderRadius: 3 }] };
  }, [data]);

  const tickerChart = useMemo(() => {
    const t = (data?.tickers || []).slice(0, 8);
    if (!t.length) return null;
    return { labels: t.map(x => x.ticker), datasets: [{ label: "ROI %", data: t.map(x => x.total_roi), backgroundColor: t.map(x => scBg(x.total_roi)), borderRadius: 3 }] };
  }, [data]);

  const roiDistChart = useMemo(() => {
    const r = data?.roi_distribution || [];
    if (!r.length) return null;
    return { labels: r.map(x => x.label), datasets: [{ label: "Trades", data: r.map(x => x.count), backgroundColor: r.map(x => bg(parseFloat(x.label) >= 0 ? G : R, 0.7)), borderRadius: 2 }] };
  }, [data]);

  const targetEffChart = useMemo(() => {
    const t = data?.target_efficiency || [];
    if (!t.length) return null;
    return { labels: t.map(x => x.target), datasets: [{ label: "Hit Rate %", data: t.map(x => x.hit_rate), backgroundColor: T, borderRadius: 3 }] };
  }, [data]);

  const firstHitChart = useMemo(() => {
    const f = data?.first_hits || [];
    if (!f.length) return null;
    return { labels: f.map(x => x.label), datasets: [{ data: f.map(x => x.total), backgroundColor: [G, R, A, B, V, T], borderWidth: 0 }] };
  }, [data]);

  const rsiFlagChart = useMemo(() => {
    const f = data?.rsi_flags || [];
    if (!f.length) return null;
    return { labels: f.map(x => x.label), datasets: [{ label: "Win Rate %", data: f.map(x => x.win_rate), backgroundColor: V, borderRadius: 3 }] };
  }, [data]);

  const avgWinLossChart = useMemo(() => {
    if (!o.avg_win && !o.avg_loss) return null;
    return { labels: ["Avg Win", "Avg Loss"], datasets: [{ data: [Math.abs(o.avg_win || 0), Math.abs(o.avg_loss || 0)], backgroundColor: [G, R], borderRadius: 3 }] };
  }, [o.avg_win, o.avg_loss]);

  const durationChart = useMemo(() => {
    const d = data?.duration_histogram || [];
    if (!d.length) return null;
    return { labels: d.map(x => x.label), datasets: [{ label: "Count", data: d.map(x => x.count), backgroundColor: B, borderRadius: 3 }] };
  }, [data]);

  const rsiScatterChart = useMemo(() => {
    const s = data?.rsi_scatter || [];
    if (!s.length) return null;
    const sampled = s.length > 300 ? s.filter((_, i) => i % Math.ceil(s.length / 300) === 0) : s;
    return { datasets: [{ label: "RSI vs ROI", data: sampled.map(r => ({ x: r.x, y: r.y })), backgroundColor: sampled.map(r => bg(r.y >= 0 ? G : R, 0.6)), pointRadius: 3 }] };
  }, [data]);

  const riskRewardChart = useMemo(() => {
    const rr = data?.risk_reward || [];
    if (!rr.length) return null;
    return { labels: rr.map(x => x.label), datasets: [{ label: "ROI %", data: rr.map(x => x.total_roi), backgroundColor: rr.map(x => scBg(x.total_roi)), borderRadius: 3 }] };
  }, [data]);

  const hourlyChart = useMemo(() => {
    const h = data?.hourly || [];
    if (!h.length) return null;
    return { labels: h.map(x => x.label), datasets: [{ label: "ROI %", data: h.map(x => x.total_roi), backgroundColor: h.map(x => scBg(x.total_roi)), borderRadius: 3 }] };
  }, [data]);

  const resolutionTimesChart = useMemo(() => {
    const rt = data?.resolution_times || [];
    if (!rt.length) return null;
    const buckets = {};
    rt.forEach(r => {
      const days = r.days || 0;
      let label;
      if (days < 1) label = "<1 day";
      else if (days < 3) label = "1-2 days";
      else if (days < 7) label = "3-6 days";
      else if (days < 14) label = "1-2 weeks";
      else if (days < 30) label = "2-4 weeks";
      else label = "1+ month";
      buckets[label] = (buckets[label] || 0) + 1;
    });
    const labels = ["<1 day", "1-2 days", "3-6 days", "1-2 weeks", "2-4 weeks", "1+ month"];
    return { labels, datasets: [{ label: "Trades", data: labels.map(l => buckets[l] || 0), backgroundColor: B, borderRadius: 3 }] };
  }, [data]);

  const contribChart = useMemo(() => {
    const tc = data?.ticker_contribution || [];
    if (!tc.length) return null;
    const topTickers = [...new Set(tc.flatMap(r => r.top.map(t => t.ticker)))].slice(0, 4);
    const colors = [G, B, V, A];
    return {
      labels: tc.map(r => r.label),
      datasets: topTickers.map((ticker, i) => ({
        label: ticker,
        data: tc.map(r => { const m = r.top.find(t => t.ticker === ticker); return m ? m.roi : 0; }),
        backgroundColor: colors[i % colors.length], borderRadius: 2,
      })),
    };
  }, [data]);

  const benchmarkChart = useMemo(() => {
    const bc = data?.benchmark_comparison;
    if (!bc?.points?.length) return null;
    let pts = bc.points;
    if (pts.length > 200) { const s = Math.ceil(pts.length / 200); pts = pts.filter((_, i) => i % s === 0 || i === pts.length - 1); }
    return {
      labels: pts.map(p => p.date),
      datasets: [
        { label: "Strategy", data: pts.map(p => p.strategy), borderColor: "#3ddc84", backgroundColor: "rgba(61,220,132,0.13)", fill: false, pointRadius: 2, tension: 0.25, borderWidth: 2.5, spanGaps: true, yAxisID: 'y' },
        { label: "NASDAQ-100 (NDX)", data: pts.map(p => p.ndx), borderColor: "#5b8cff", backgroundColor: "#5b8cff", fill: false, pointRadius: 1.5, tension: 0.25, borderWidth: 1.75, spanGaps: true, yAxisID: 'y1' },
        { label: "S&P 500 (SPX)", data: pts.map(p => p.spx), borderColor: "#ffb454", backgroundColor: "#ffb454", fill: false, pointRadius: 1.5, tension: 0.25, borderWidth: 1.75, spanGaps: true, yAxisID: 'y1' },
        { label: "Dow Jones (DJI)", data: pts.map(p => p.dji), borderColor: "#ff6b6b", backgroundColor: "#ff6b6b", fill: false, pointRadius: 1.5, tension: 0.25, borderWidth: 1.75, spanGaps: true, yAxisID: 'y1' },
      ],
    };
  }, [data]);

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-ics-gray">Loading Nasdaq analytics...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <BarChart3 className="w-16 h-16 text-ics-gray/30 mx-auto mb-4" />
        <p className="text-ics-gray text-lg">No Nasdaq data available.</p>
        <p className="text-ics-gray text-sm mt-2">Enable public access in the admin panel.</p>
      </div>
    );
  }

  if (data.filtered_trades === 0) {
    return (
      <div className="text-center py-16">
        <BarChart3 className="w-16 h-16 text-ics-gray/30 mx-auto mb-4" />
        <p className="text-ics-white text-lg font-semibold mb-2">No Trades Found</p>
        <p className="text-ics-gray text-sm">No trades match the current filters. Try adjusting the date range, ticker, or outcome filters.</p>
        <p className="text-ics-gray text-xs mt-2">Total trades available: {data.total_trades || 0}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Brand Header */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-ics-white">{data.brand_name || "Investment Circle Sharia"}</h3>
        <p className="text-ics-gray text-sm">{data.mode?.toUpperCase()} Bot &mdash; {fmt(data.total_trades)} total trades | {fmt(data.filtered_trades)} filtered | Stats: {data.stats_period}</p>
      </div>

      {/* Benchmark Comparison */}
      {benchmarkChart && (
        <div className="bg-[#0f1420] rounded-2xl border border-[#2a3350] p-5">
          <div className="text-lg font-bold text-ics-white">Strategy vs NDX / SPX / DJI</div>
          <div className="text-xs text-ics-gray mb-4">Time-series comparison, normalized to 100 at the start of the trade log &nbsp;•&nbsp; {data.benchmark_comparison?.first_month || data.benchmark_comparison?.first_date || 'inception'} onwards</div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              ["Strategy", data.benchmark_comparison?.summary?.strategy_return, "#3ddc84"],
              ["NASDAQ-100 (NDX)", data.benchmark_comparison?.summary?.ndx_return, "#5b8cff"],
              ["S&P 500 (SPX)", data.benchmark_comparison?.summary?.spx_return, "#ffb454"],
              ["Dow Jones (DJI)", data.benchmark_comparison?.summary?.dji_return, "#ff6b6b"],
            ].map(([l, v, dot]) => (
              <div key={l} className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
                <div className="text-xs text-ics-gray flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: dot }} />
                  {l}
                </div>
                <div className={`text-[22px] font-bold mt-1.5 ${sc(v)}`}>{fmtS(v, "%")}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4 mb-4">
            <div className="text-sm font-bold text-ics-white mb-1">Growth of $100, indexed at {data.benchmark_comparison?.first_month || data.benchmark_comparison?.first_date || 'inception'}</div>
            <div className="text-xs text-ics-gray mb-3">Strategy line built from trade log equity. Index lines are daily closes, indexed to the same start date.</div>
            <div style={{ height: 320 }}><Line data={benchmarkChart} options={{
              responsive: true, maintainAspectRatio: true,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { labels: { color: "#e7ebf5", boxWidth: 14, boxHeight: 2, padding: 14, usePointStyle: true } },
                datalabels: { display: false },
                tooltip: {
                  backgroundColor: "#1d2436", borderColor: "#2a3350", borderWidth: 1,
                  callbacks: {
                    label: (ctx) => {
                      const val = ctx.parsed.y;
                      const chg = val != null ? (val - 100).toFixed(2) : '-';
                      return ` ${ctx.dataset.label}: ${val != null ? val.toFixed(2) : '-'} (${chg >= 0 ? '+' : ''}${chg}%)`;
                    },
                  },
                },
              },
              scales: {
                x: { grid: { color: "#2a3350" }, ticks: { color: "#9aa4c2", font: { size: 10 }, maxTicksLimit: 12 } },
                y: { type: 'linear', display: true, position: 'left', grid: { color: "#2a3350" }, ticks: { color: "#9aa4c2", callback: (v) => v.toFixed(0) }, title: { display: true, text: 'Strategy', color: '#3ddc84', font: { size: 11 } } },
                y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, ticks: { color: "#9aa4c2", callback: (v) => v.toFixed(0) }, title: { display: true, text: 'Indices', color: '#5b8cff', font: { size: 11 } } },
              },
            }} /></div>
          </div>

          {/* Daily Comparison Table */}
          <div className="bg-[#171d2b] border border-[#2a3350] rounded-xl p-4">
            <div className="text-sm font-bold text-ics-white mb-1">Daily comparison table</div>
            <div className="text-xs text-ics-gray mb-3">Indexed value (100 = start) for each series, by date</div>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 z-10"><tr className="border-b border-[#2a3350] bg-[#171d2b]">
                  <th className="text-left py-2 pr-2.5 text-ics-gray text-[11px] uppercase tracking-wider">Date</th>
                  <th className="text-right py-2 px-2.5 text-ics-gray text-[11px] uppercase tracking-wider">Strategy</th>
                  <th className="text-right py-2 px-2.5 text-ics-gray text-[11px] uppercase tracking-wider">NDX</th>
                  <th className="text-right py-2 px-2.5 text-ics-gray text-[11px] uppercase tracking-wider">SPX</th>
                  <th className="text-right py-2 px-2.5 text-ics-gray text-[11px] uppercase tracking-wider">DJI</th>
                  <th className="text-right py-2 px-2.5 text-ics-gray text-[11px] uppercase tracking-wider">Strategy vs NDX</th>
                  <th className="text-right py-2 px-2.5 text-ics-gray text-[11px] uppercase tracking-wider">Strategy vs SPX</th>
                  <th className="text-right py-2 px-2.5 text-ics-gray text-[11px] uppercase tracking-wider">Strategy vs DJI</th>
                </tr></thead>
                <tbody>
                  {(data.benchmark_comparison?.points || []).map((pt, i) => {
                    const s = pt.strategy, n = pt.ndx, p = pt.spx, j = pt.dji;
                    const vsN = s != null && n != null ? s - n : null;
                    const vsP = s != null && p != null ? s - p : null;
                    const vsJ = s != null && j != null ? s - j : null;
                    const vsCls = (v) => v == null ? "text-ics-gray" : sc(v);
                    return (
                      <tr key={i} className="border-b border-[#2a3350] hover:bg-[#1d2436]">
                        <td className="py-1.5 pr-2.5 text-ics-white">{pt.date}</td>
                        <td className="py-1.5 px-2.5 text-right text-ics-gray">{fmt(s, 2)}</td>
                        <td className="py-1.5 px-2.5 text-right text-ics-gray">{fmt(n, 2)}</td>
                        <td className="py-1.5 px-2.5 text-right text-ics-gray">{fmt(p, 2)}</td>
                        <td className="py-1.5 px-2.5 text-right text-ics-gray">{fmt(j, 2)}</td>
                        <td className={`py-1.5 px-2.5 text-right ${vsCls(vsN)}`}>{fmtS(vsN, "%")}</td>
                        <td className={`py-1.5 px-2.5 text-right ${vsCls(vsP)}`}>{fmtS(vsP, "%")}</td>
                        <td className={`py-1.5 px-2.5 text-right ${vsCls(vsJ)}`}>{fmtS(vsJ, "%")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-ics-gray mt-3 leading-relaxed">
              <b>How the strategy line was built:</b> the strategy's indexed value is derived from the cumulative
              ROI% of every resolved trade in the current filter, normalized so the first trade date equals 100.<br /><br />
              <b>How the benchmark lines were built:</b> NDX, SPX and DJI daily closes come from Yahoo Finance
              (<code>^NDX</code>, <code>^GSPC</code>, <code>^DJI</code>). Non-trading days carry forward the last
              known close rather than fabricating a value.
            </div>
          </div>
        </div>
      )}

      {/* KPI Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Signals" value={fmt(o.total)} hint={`${o.resolved||0} resolved`} />
        <Kpi label="Win Rate" value={fmtP(o.win_rate)} hint={`${o.tp_hits||0} W / ${o.sl_hits||0} SL`} color={sc((o.win_rate||0)-50)} />
        <Kpi label="Avg ROI" value={fmtS(o.avg_roi, "%")} color={sc(o.avg_roi)} />
        <Kpi label="Profit Factor" value={fmt(o.profit_factor, 2)} color={sc((o.profit_factor||0)-1)} />
      </div>

      {/* KPI Row 2: Extended Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total ROI" value={fmtS(o.total_roi, "%")} hint="Sum of all trades" color={sc(o.total_roi)} />
        <Kpi label="Compounded" value={o.compounded_roi > 99999 ? ">99,999%" : fmtS(o.compounded_roi, "%")} hint="Theoretical (100% reinvest)" color={sc(o.compounded_roi)} />
        <Kpi label="Avg Win" value={fmtS(o.avg_win_roi, "%")} color="text-green-400" />
        <Kpi label="Avg Loss" value={fmtS(o.avg_loss_roi, "%")} color="text-red-400" />
      </div>

      {/* Equity Summary */}
      <div className="bg-white/5 rounded-xl p-3 border border-white/10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Peak Equity" value={fmt(eq.peak_equity, 1)+"%"} color="text-green-400" />
          <Kpi label="Max Drawdown" value={fmt(eq.max_drawdown, 1)+"%"} color="text-red-400" />
          <Kpi label="Best Trade" value={fmtS(o.best_trade?.roi, "%")} hint={o.best_trade?.ticker||"-"} color="text-green-400" />
          <Kpi label="Worst Trade" value={fmtS(o.worst_trade?.roi, "%")} hint={o.worst_trade?.ticker||"-"} color="text-red-400" />
        </div>
      </div>

      {/* Primary Period + Outcome */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Period Performance" subtitle={data.primary_period?.title} className="lg:col-span-2">
          <div style={{ height: 200 }}>{monthlyChart ? <Bar data={monthlyChart} options={chartOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Outcome Mix">
          <div style={{ height: 200 }}>{outcomeChart ? <div className="flex justify-center"><Doughnut data={outcomeChart} options={{ responsive: true, maintainAspectRatio: true, cutout: "65%", plugins: { legend: { position: "bottom", labels: { boxWidth: 10, color: "#94a3b8" } }, datalabels: { display: false } } }} /></div> : noDataMsg()}</div>
        </Panel>
      </div>

      {/* Equity + Run Leaders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Cumulative Equity Growth" subtitle="%" className="lg:col-span-2">
          <div style={{ height: 200 }}>{equityChart ? <Line data={equityChart} options={chartOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Run Leaders">
          <div className="space-y-2 text-sm">
            {[["Best Ticker", data.best_ticker?.ticker, data.best_ticker?.total_roi], ["Best Month", data.best_month?.label, null], ["Best Weekday", data.best_weekday?.label, null], ["Best RSI Zone", data.best_rsi?.label, null], ["Best Trade", `${o.best_trade?.ticker||"-"} ${fmtS(o.best_trade?.roi, "%")}`, null], ["Worst Trade", `${o.worst_trade?.ticker||"-"} ${fmtS(o.worst_trade?.roi, "%")}`, null]].map(([l, v, extra]) => (
              <div key={l} className="flex justify-between items-center border-b border-white/5 pb-1.5">
                <span className="text-ics-gray text-xs">{l}</span>
                <span className="text-ics-white font-bold text-xs">{v}{extra != null ? ` (${fmtS(extra, "%")})` : ""}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Row 5: 4-col compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Panel title="Daily ROI Growth" className="compact">
          <div style={{ height: 130 }}>{dailyChart ? <Bar data={dailyChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Hourly Performance" className="compact">
          <div style={{ height: 130 }}>{hourlyChart ? <Bar data={hourlyChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Drawdown Map" className="compact">
          <div style={{ height: 130 }}>{drawdownChart ? <Line data={drawdownChart} options={compactOpts({ scales: { y: { max: 0 } } })} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Resolution Times" className="compact">
          <div style={{ height: 130 }}>{resolutionTimesChart ? <Bar data={resolutionTimesChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
      </div>

      {/* Row 5b: 4-col compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Panel title="RSI Entry Map" className="compact">
          <div style={{ height: 130 }}>{rsiChart ? <Bar data={rsiChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Weekday Performance" className="compact">
          <div style={{ height: 130 }}>{weekdayChart ? <Bar data={weekdayChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Top Performers" className="compact">
          <div className="space-y-1 text-xs">
            {topTickers.slice(0, 5).map((t, i) => (
              <div key={t.ticker} className="flex justify-between items-center">
                <span className="text-ics-gray">{i+1}. {t.ticker}</span>
                <span className={`font-bold ${sc(t.total_roi)}`}>{fmtS(t.total_roi, "%")}</span>
              </div>
            ))}
            {topTickers.length === 0 && <div className="text-ics-gray text-center py-2">No data</div>}
          </div>
        </Panel>
        <Panel title="Bottom Performers" className="compact">
          <div className="space-y-1 text-xs">
            {bottomTickers.slice(0, 5).map((t, i) => (
              <div key={t.ticker} className="flex justify-between items-center">
                <span className="text-ics-gray">{i+1}. {t.ticker}</span>
                <span className={`font-bold ${sc(t.total_roi)}`}>{fmtS(t.total_roi, "%")}</span>
              </div>
            ))}
            {bottomTickers.length === 0 && <div className="text-ics-gray text-center py-2">No data</div>}
          </div>
        </Panel>
      </div>

      {/* Row 6: 4-col compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Panel title="Ticker Leaderboard" className="compact">
          <div style={{ height: 130 }}>{tickerChart ? <Bar data={tickerChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="ROI Distribution" className="compact">
          <div style={{ height: 130 }}>{roiDistChart ? <Bar data={roiDistChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Target Efficiency" className="compact">
          <div style={{ height: 130 }}>{targetEffChart ? <Bar data={targetEffChart} options={compactOpts({ scales: { y: { min: 0, max: 100 } } })} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="First Hit Map" className="compact">
          <div style={{ height: 130 }}>{firstHitChart ? <div className="flex justify-center"><Doughnut data={firstHitChart} options={{ responsive: true, maintainAspectRatio: true, cutout: "60%", plugins: { legend: { display: false }, datalabels: { display: false } } }} /></div> : noDataMsg()}</div>
        </Panel>
      </div>

      {/* Row 7: 4-col compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Panel title="RSI Cohort Flags" className="compact">
          <div style={{ height: 130 }}>{rsiFlagChart ? <Bar data={rsiFlagChart} options={compactOpts({ scales: { y: { min: 0, max: 100 } } })} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Avg Win vs Loss" className="compact">
          <div style={{ height: 130 }}>{avgWinLossChart ? <Bar data={avgWinLossChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Trade Duration" className="compact">
          <div style={{ height: 130 }}>{durationChart ? <Bar data={durationChart} options={compactOpts()} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Streaks" className="compact">
          <div className="flex justify-around items-center h-full py-2">
            <div className="text-center">
              <div className="text-[10px] text-ics-gray font-bold uppercase">Win Streak</div>
              <div className="text-2xl font-extrabold text-green-400">{streaks.longest_win || 0}</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-center">
              <div className="text-[10px] text-ics-gray font-bold uppercase">Loss Streak</div>
              <div className="text-2xl font-extrabold text-red-400">{streaks.longest_loss || 0}</div>
            </div>
          </div>
        </Panel>
      </div>

      {/* Row 8: Scatter + Risk Reward */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="RSI Sweet Spot Scatter" subtitle="Entry RSI vs ROI">
          <div style={{ height: 220 }}>{rsiScatterChart ? <Scatter data={rsiScatterChart} options={{ ...chartOpts(), scales: { x: { title: { display: true, text: "Entry RSI", color: "#94a3b8" }, grid: { color: GR } }, y: { title: { display: true, text: "ROI %", color: "#94a3b8" }, grid: { color: GR } } } }} /> : noDataMsg()}</div>
        </Panel>
        <Panel title="Risk / Reward Zones">
          <div style={{ height: 220 }}>{riskRewardChart ? <Bar data={riskRewardChart} options={chartOpts()} /> : noDataMsg()}</div>
        </Panel>
      </div>

      {/* Row 9: Quality Table + Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title={`${data.quality_label || "Monthly"} Quality Table`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-white/10">
                <th className="text-left py-1.5 text-ics-gray">Period</th>
                <th className="text-right py-1.5 text-ics-gray">Trades</th>
                <th className="text-right py-1.5 text-ics-gray">Win%</th>
                <th className="text-right py-1.5 text-ics-gray">PF</th>
                <th className="text-right py-1.5 text-ics-gray">ROI%</th>
                <th className="text-right py-1.5 text-ics-gray">Exp</th>
              </tr></thead>
              <tbody>
                {(data.quality || []).slice(0, 12).map(r => (
                  <tr key={r.key} className="border-b border-white/5">
                    <td className="py-1 text-ics-white font-semibold">{r.label}</td>
                    <td className="py-1 text-right text-ics-gray">{r.total}</td>
                    <td className="py-1 text-right text-ics-gray">{fmtP(r.win_rate)}</td>
                    <td className="py-1 text-right text-ics-gray">{fmt(r.profit_factor, 1)}</td>
                    <td className={`py-1 text-right font-bold ${sc(r.total_roi)}`}>{fmtS(r.total_roi, "%")}</td>
                    <td className="py-1 text-right text-ics-gray">{fmt(r.expectancy, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Ticker / Month Heatmap">
          <div className="overflow-x-auto">
            {data.ticker_monthly_table && data.heatmap_months ? (
              <table className="w-full text-[10px]">
                <thead><tr className="border-b border-white/10">
                  <th className="text-left py-1 text-ics-gray sticky left-0 bg-ics-navy-light">Ticker</th>
                  {data.heatmap_months.slice(-6).map(m => <th key={m} className="text-center py-1 text-ics-gray px-1">{m}</th>)}
                  <th className="text-right py-1 text-ics-gray">Total</th>
                </tr></thead>
                <tbody>
                  {data.ticker_monthly_table.slice(0, 15).map(row => {
                    const maxHeat = 50;
                    return (
                      <tr key={row.ticker} className="border-b border-white/5">
                        <td className="py-0.5 text-ics-white font-bold sticky left-0 bg-ics-navy-light">{row.ticker}</td>
                        {data.heatmap_months.slice(-6).map(m => {
                          const cell = row.months[m];
                          const roi = cell?.roi ?? null;
                          const heat = roi !== null ? Math.min(100, Math.abs(roi) / maxHeat * 100) : 0;
                          return (
                            <td key={m} className="text-center py-0.5 px-1 rounded" style={{
                              backgroundColor: roi === null ? "transparent" : roi >= 0 ? `rgba(34,197,94,${heat/100*0.4+0.05})` : `rgba(239,68,68,${heat/100*0.4+0.05})`,
                              color: roi === null ? "#475569" : roi >= 0 ? "#22c55e" : "#ef4444",
                            }}>
                              {roi !== null ? fmtS(roi, "%") : "-"}
                            </td>
                          );
                        })}
                        <td className={`text-right py-0.5 font-bold ${sc(row.total_roi)}`}>{fmtS(row.total_roi, "%")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : <div className="text-ics-gray text-sm text-center py-8">No heatmap data</div>}
          </div>
        </Panel>
      </div>

      {/* Row 10: Ticker Contribution */}
      {contribChart && (
        <Panel title="Ticker Contribution by Month" subtitle="Top contributors per period">
          <div style={{ height: 220 }}><Bar data={contribChart} options={chartOpts({ scales: { x: { stacked: true, grid: { color: GR } }, y: { stacked: true, grid: { color: GR } } } })} /></div>
        </Panel>
      )}

      {/* Row 11: Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Panel title="Best Performers">
          <div className="space-y-1.5 text-xs">
            {[["Month", data.best_month?.label, data.best_month?.total_roi], ["Weekday", data.best_weekday?.label, null], ["Ticker", data.best_ticker?.ticker, data.best_ticker?.total_roi], ["RSI Zone", data.best_rsi?.label, null]].map(([l, v, r]) => (
              <div key={l} className="flex justify-between"><span className="text-ics-gray">{l}:</span><span className="text-ics-white font-bold">{v||"-"}{r!=null?` (${fmtS(r, "%")})`:""}</span></div>
            ))}
          </div>
        </Panel>
        <Panel title="Risk-Reward Summary">
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-ics-gray">Avg Win:</span><span className="text-green-400 font-bold">{fmtS(data.risk_reward_summary?.avg_win, "%")}</span></div>
            <div className="flex justify-between"><span className="text-ics-gray">Avg Loss:</span><span className="text-red-400 font-bold">{fmtS(data.risk_reward_summary?.avg_loss, "%")}</span></div>
            <div className="flex justify-between"><span className="text-ics-gray">Payoff Ratio:</span><span className="text-ics-white font-bold">{fmt(data.risk_reward_summary?.payoff_ratio, 2)}</span></div>
            <div className="flex justify-between"><span className="text-ics-gray">Profit Factor:</span><span className="text-ics-white font-bold">{fmt(data.risk_reward_summary?.profit_factor, 2)}</span></div>
          </div>
        </Panel>
        <Panel title="Resolution Times">
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-ics-gray">Min:</span><span className="text-ics-white font-bold">{fmt(resSummary.min)} days</span></div>
            <div className="flex justify-between"><span className="text-ics-gray">Max:</span><span className="text-ics-white font-bold">{fmt(resSummary.max)} days</span></div>
            <div className="flex justify-between"><span className="text-ics-gray">Avg:</span><span className="text-ics-white font-bold">{fmt(resSummary.avg_days, 1)} days</span></div>
          </div>
        </Panel>
      </div>

      {/* Ticker Leaderboard Table */}
      {tickers.length > 0 && (
        <Panel title="Ticker Leaderboard" subtitle={`Top ${Math.min(tickers.length, 15)} performers`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-white/10">
                <th className="text-left py-1.5 text-ics-gray">#</th>
                <th className="text-left py-1.5 text-ics-gray">Ticker</th>
                <th className="text-right py-1.5 text-ics-gray">Trades</th>
                <th className="text-right py-1.5 text-ics-gray">Win%</th>
                <th className="text-right py-1.5 text-ics-gray">Avg ROI</th>
                <th className="text-right py-1.5 text-ics-gray">Total ROI</th>
              </tr></thead>
              <tbody>
                {tickers.slice(0, 15).map((t, i) => (
                  <tr key={t.ticker} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-1.5 text-ics-gray">{i + 1}</td>
                    <td className="py-1.5 text-ics-white font-bold">{t.ticker}</td>
                    <td className="py-1.5 text-right text-ics-gray">{t.total}</td>
                    <td className="py-1.5 text-right text-ics-gray">{fmtP(t.win_rate)}</td>
                    <td className={`py-1.5 text-right font-semibold ${sc(t.avg_roi)}`}>{fmtS(t.avg_roi, "%")}</td>
                    <td className={`py-1.5 text-right font-bold ${sc(t.total_roi)}`}>{fmtS(t.total_roi, "%")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* Footer */}
      <div className="text-center text-ics-gray/40 text-xs pt-4 border-t border-white/5">
        Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : "-"} | {data.brand_name}
      </div>
    </div>
  );
}
